/**
 * TOSS Web Application - Auth API Service
 *
 * API functions for authentication endpoints:
 * - Login with email/password
 * - Self-registration (Mechanic/Intern only)
 * - Token refresh
 * - Password recovery (forgot/reset)
 * - Mentor verification
 * - FCM token management
 * - Password change
 * - Logout
 */

import apiClient from './client';
import { TokenStorage, getAudienceApiBase } from './interceptors';
import {
  API_ENDPOINTS,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  VerifyOtpRequest,
  VerifyOtpResponse,
  ResetPasswordRequest,
  VerifyMentorRequest,
  VerifyMentorResponse,
  SuccessResponse,
  User,
} from '@/types';

// ============================================================================
// Types
// ============================================================================

/**
 * Change password request
 */
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * Update FCM token request
 */
export interface UpdateFcmTokenRequest {
  fcmToken: string;
  /** Platform label: "web", "android-pwa", "ios-pwa", "desktop". Optional for backward compat. */
  platform?: string;
  /** Raw User-Agent string of the device. Optional. */
  userAgent?: string;
}

/**
 * Delete FCM token request (called on logout to unregister a device token)
 */
export interface DeleteFcmTokenRequest {
  fcmToken: string;
}

// ============================================================================
// Auth API Functions
// ============================================================================

/**
 * Login with email and password
 *
 * @param credentials - Login credentials (email, password)
 * @returns Login response with tokens and user data
 *
 * @example
 * ```ts
 * const { accessToken, refreshToken, user } = await authApi.login({
 *   email: 'user@example.com',
 *   password: 'password123'
 * });
 * ```
 */
export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  const response = await apiClient.post<LoginResponse>(
    API_ENDPOINTS.AUTH.LOGIN,
    credentials
  );
  return response.data;
}

/**
 * Register a new user (Mechanic or Intern roles only)
 *
 * @param data - Registration data
 * @returns Registration response with tokens and user data (enables auto-login)
 *
 * @example
 * ```ts
 * const { accessToken, refreshToken, user } = await authApi.register({
 *   fullName: 'John Doe',
 *   email: 'john@example.com',
 *   phoneNumber: '+79001234567',
 *   password: 'password123',
 *   role: UserRole.Mechanic,
 *   ptoId: 'pto-guid',
 *   isRulesAccepted: true
 * });
 * ```
 */
export async function register(data: RegisterRequest): Promise<RegisterResponse> {
  const response = await apiClient.post<RegisterResponse>(
    API_ENDPOINTS.AUTH.REGISTER,
    data
  );
  return response.data;
}

/**
 * Refresh access token using refresh token
 *
 * @param refreshToken - Current refresh token
 * @returns New tokens
 *
 * @note This is typically handled automatically by the axios interceptor.
 * Use this directly only for manual token refresh scenarios.
 */
export async function refreshToken(
  refreshToken: string
): Promise<RefreshTokenResponse> {
  const response = await apiClient.post<RefreshTokenResponse>(
    API_ENDPOINTS.AUTH.REFRESH,
    { refreshToken } as RefreshTokenRequest
  );
  return response.data;
}

/**
 * Request password reset OTP code
 *
 * @param email - User's email address
 * @returns Success response (always returns success to prevent email enumeration)
 *
 * @example
 * ```ts
 * await authApi.forgotPassword({ email: 'user@example.com' });
 * ```
 */
export async function forgotPassword(
  data: ForgotPasswordRequest
): Promise<ForgotPasswordResponse> {
  const response = await apiClient.post<ForgotPasswordResponse>(
    API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
    data
  );
  return response.data;
}

/**
 * Verify OTP code sent via email
 *
 * @param data - OTP verification data (email, otpCode)
 * @returns Verification result with reset token if successful
 *
 * @example
 * ```ts
 * const { success, resetToken } = await authApi.verifyOtp({
 *   email: 'user@example.com',
 *   otpCode: '123456'
 * });
 * ```
 */
export async function verifyOtp(
  data: VerifyOtpRequest
): Promise<VerifyOtpResponse> {
  const response = await apiClient.post<VerifyOtpResponse>(
    API_ENDPOINTS.AUTH.VERIFY_OTP,
    data
  );
  return response.data;
}

/**
 * Reset password with reset token from OTP verification
 *
 * @param data - Reset password data (email, resetToken, newPassword)
 * @returns Success response
 *
 * @example
 * ```ts
 * await authApi.resetPassword({
 *   email: 'user@example.com',
 *   resetToken: 'token-from-verify-otp',
 *   newPassword: 'NewPassword123!'
 * });
 * ```
 */
export async function resetPassword(
  data: ResetPasswordRequest
): Promise<SuccessResponse> {
  const response = await apiClient.post<SuccessResponse>(
    API_ENDPOINTS.AUTH.RESET_PASSWORD,
    data
  );
  return response.data;
}

/**
 * Verify mentor exists (for registration)
 *
 * @param data - Mentor verification data (mentorId or fullName)
 * @returns Verification result with mentor data if found
 *
 * @example
 * ```ts
 * const { found, mentor } = await authApi.verifyMentor({
 *   mentorId: 'mentor-guid'
 * });
 *
 * // Or by name
 * const { found, mentor } = await authApi.verifyMentor({
 *   fullName: 'John Smith'
 * });
 * ```
 */
export async function verifyMentor(
  data: VerifyMentorRequest
): Promise<VerifyMentorResponse> {
  const response = await apiClient.post<VerifyMentorResponse>(
    API_ENDPOINTS.AUTH.VERIFY_MENTOR,
    data
  );
  return response.data;
}

/**
 * Get current authenticated user
 *
 * @returns Current user data
 *
 * @example
 * ```ts
 * const user = await authApi.getCurrentUser();
 * ```
 */
export async function getCurrentUser(): Promise<User> {
  const response = await apiClient.get<User>(API_ENDPOINTS.USERS.ME);
  return response.data;
}

// From here down, every endpoint is called from an already-authenticated session
// (change-password, FCM registration, logout) and resolves against
// `getAudienceApiBase()` — the internal `/api/lk` default, or `/api/client` for a
// client session. `login`/`register`/`forgotPassword`/`verifyOtp`/`resetPassword`/
// `verifyMentor` above stay hardcoded to `/api/lk/*`: register/verify-mentor have no
// client equivalent at all (self-registration doesn't exist for that audience), and
// resolving "current audience" is meaningless before a login has actually
// succeeded — a client-facing login form belongs in its own API module (Р1),
// not as a branch here.

/**
 * Change password for current user
 *
 * @param data - Current password, new password, and confirmation
 * @returns Success response
 *
 * @example
 * ```ts
 * await authApi.changePassword({
 *   currentPassword: 'oldPassword123',
 *   newPassword: 'newPassword456',
 *   confirmPassword: 'newPassword456'
 * });
 * ```
 */
export async function changePassword(
  data: ChangePasswordRequest
): Promise<SuccessResponse> {
  const response = await apiClient.post<SuccessResponse>(
    `${getAudienceApiBase()}/auth/change-password`,
    data
  );
  return response.data;
}

/**
 * Update FCM token for push notifications
 *
 * Registers the FCM token for the current device. Backend stores this as
 * a `DeviceToken` row (multi-device per user). Platform and user-agent are
 * optional — backend falls back to `"legacy"` when platform is missing.
 *
 * @param data - FCM token plus optional platform/userAgent metadata
 * @returns Success response
 *
 * @example
 * ```ts
 * await authApi.updateFcmToken({
 *   fcmToken: 'firebase-fcm-token',
 *   platform: 'android-pwa',
 *   userAgent: navigator.userAgent,
 * });
 * ```
 */
export async function updateFcmToken(
  data: UpdateFcmTokenRequest
): Promise<SuccessResponse> {
  // No-op for the client portal: it has no push notifications, and ClientAuthController has no
  // fcm-token action. Without this branch the first portal session that is granted notification
  // permission POSTs to a route that does not exist — a 404 the push hook swallows and logs, so
  // it would show up as noise rather than a failure. Revisit if the portal ever gets push.
  if (TokenStorage.getAudience() === 'client') {
    return { success: true };
  }

  const response = await apiClient.post<SuccessResponse>(
    `${getAudienceApiBase()}/auth/fcm-token`,
    data
  );
  return response.data;
}

/**
 * Unregister an FCM token for the current device.
 *
 * Called on logout (before `/api/auth/logout`, while the JWT is still valid)
 * to remove only this device's token. Other devices remain subscribed.
 *
 * Backend endpoint: `POST /api/auth/fcm-token/logout`
 *
 * @param data - FCM token to unregister
 */
export async function deleteFcmToken(
  data: DeleteFcmTokenRequest
): Promise<void> {
  // See updateFcmToken: the portal has no push, so there is nothing to unregister.
  if (TokenStorage.getAudience() === 'client') {
    return;
  }

  await apiClient.post(`${getAudienceApiBase()}/auth/fcm-token/logout`, data);
}

/**
 * Logout current user
 *
 * Clears tokens from storage and optionally notifies the server
 *
 * @example
 * ```ts
 * await authApi.logout();
 * ```
 */
export async function logout(): Promise<void> {
  try {
    // Notify server to invalidate refresh token
    await apiClient.post(`${getAudienceApiBase()}/auth/logout`);
  } catch {
    // Ignore errors - we're logging out anyway
  } finally {
    // Always clear local tokens
    TokenStorage.clearTokens();
  }
}

// ============================================================================
// Namespace Export
// ============================================================================

/**
 * Auth API namespace for convenient imports
 *
 * @example
 * ```ts
 * import { authApi } from '@/api/auth.api';
 *
 * const response = await authApi.login({ email, password });
 * ```
 */
export const authApi = {
  login,
  register,
  refreshToken,
  forgotPassword,
  verifyOtp,
  resetPassword,
  verifyMentor,
  getCurrentUser,
  changePassword,
  updateFcmToken,
  deleteFcmToken,
  logout,
} as const;

export default authApi;
